<script setup lang="ts">
import { QInput } from 'quasar'
import { computed, ref, toRef, watch } from 'vue'
import { Color } from '@previo/codebooks'
import { usePrevioCore } from '@previo/core'
import type { PValidationRule } from '../../types/types'
import PDivider from '../divider/PDivider.vue'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextStandard from '../typography/body/PTextStandard.vue'
import { usePrevioDS } from '../../composables/helpers'
import { PTextInputFieldEnum } from './types'
import InputFieldLabel from './components/LabelInputField.vue'
import { useNumberInput } from './composable/useNumberInput'

type Props = {
  label?: null | string
  tooltip?: null | string
  placeholder?: null | string
  hint?: null | string
  rules?: PValidationRule[]
  type?: PTextInputFieldEnum
  startIcon?: null | string
  endIcon?: null | string
  endIconClickable?: boolean
  endText?: null | string
  endEndIcon?: null | string
  loading?: boolean
  disable?: boolean
  readonly?: boolean
  debounce?: null | number
  autofocus?: boolean
  errorMessage?: null | string
  maxlength?: null | number
  decimals?: null | number
  cy: string
}

type Emits = {
  (e: 'click', evt: Event): void
  (e: 'endIconClick', evt: Event): void
  (e: 'blur', evt: Event): void
  (e: 'keydown', evt: Event): void
}

const model = defineModel<string | number | null>({ default: null })

const props = withDefaults(defineProps<Props>(), {
  label: null,
  tooltip: null,
  endIconClickable: false,
  loading: false,
  disable: false,
  readonly: false,
  debounce: 0,
  autofocus: false,
  errorMessage: null,
  decimals: null,
  type() {
    return PTextInputFieldEnum.TEXT
  },
  rules() {
    return []
  },
})

const emit = defineEmits<Emits>()

const core = usePrevioCore()
const ds = usePrevioDS()
const qInputComponent = ref<InstanceType<typeof QInput>>()

const isRequired = computed(() => {
  return props.rules.includes(ds.rules.required)
})

const isPositiveNumberWithZero = computed(() => {
  return (
    props.rules.includes(ds.rules.positiveNumber) ||
    props.rules.includes(ds.rules.positiveNumberWithZero)
  )
})

const hasAnyErrors = computed<boolean>(() => {
  return !!props.errorMessage
})

const isDecimal = computed<boolean>(() => {
  return props.type === PTextInputFieldEnum.DECIMAL
})

const qType = computed(() => {
  return props.type === PTextInputFieldEnum.DECIMAL ? PTextInputFieldEnum.TEXT : props.type
})

const { inputValue, inputRules, onBeforeInput, formatOnBlur } = useNumberInput({
  model,
  isDecimal,
  decimals: toRef(props, 'decimals'),
  rules: toRef(props, 'rules'),
  locale: computed(() => core.useLocale().get()),
  nativeElement: () => qInputComponent.value?.nativeEl,
})

const onBlur = (event: Event): void => {
  formatOnBlur()

  emit('blur', event)
}

watch(model, (val) => {
  if (val === '') {
    model.value = null
  }
})

const focus = (): void => {
  qInputComponent.value?.focus()
}

defineExpose({ focus })
</script>

<template>
  <slot name="label">
    <input-field-label :label="label" :is-required="isRequired" :tooltip="tooltip" />
  </slot>

  <q-input
    ref="qInputComponent"
    v-model="inputValue"
    :rules="inputRules"
    :placeholder="placeholder"
    :hint="hint ?? undefined"
    :autofocus="autofocus"
    :error="hasAnyErrors"
    :error-message="errorMessage ?? undefined"
    :bg-color="Color.WHITE"
    :type="qType"
    :inputmode="isDecimal ? 'decimal' : undefined"
    :loading="loading"
    :disable="disable"
    :readonly="readonly"
    :class="{ 'ds-input-field--readonly': readonly }"
    clear-icon="fa-light fa-xmark"
    :data-cy="cy"
    autocomplete="nope"
    :debounce="debounce ?? 0"
    outlined
    lazy-rules
    :min="isPositiveNumberWithZero ? 0 : undefined"
    :maxlength="maxlength ? maxlength : undefined"
    no-error-icon
    hide-bottom-space
    @beforeinput="onBeforeInput"
    @blur="onBlur"
    @click="(e: Event) => $emit('click', e)"
    @keydown="(e: Event) => $emit('keydown', e)"
  >
    <template v-if="startIcon" #prepend>
      <p-icon :icon="startIcon" :color="Color.GREY_600" />
    </template>

    <template v-if="(endIcon || endText || endEndIcon) && !loading" #append>
      <p-icon
        v-if="endIcon"
        :icon="endIcon"
        :color="Color.GREY_600"
        :size="15"
        :cursor-pointer="endIconClickable"
        @click="(e: Event) => $emit('endIconClick', e)"
      />

      <template v-if="endText || endEndIcon">
        <p-space v-if="endIcon" :mobile="PSpaceSize.SM" />
        <p-divider vertical inset />
        <p-space :mobile="PSpaceSize.SM" />
        <p-text-standard v-if="endText" :text="endText" :color="Color.GREY_500" />
        <p-icon v-if="endEndIcon" :icon="endEndIcon" :color="Color.GREY_600" :size="15" />
      </template>
    </template>
  </q-input>
</template>
